
import { effect, inject, Injectable, Signal, signal, untracked } from '@angular/core';
import { first } from 'rxjs/operators';

// Services
import { AttachmentService } from 'src/app/services/api/attachments/attachment.service';
import { LabelProjectService } from 'src/app/services/api/labelProjects/labelProject.service';
import { LabelService } from 'src/app/services/api/labels/label.service';
import { ProjectService } from 'src/app/services/api/projects/project.service';

// Interfaces
import { Label } from 'src/app/interfaces/api/label';
import { LabelProject } from 'src/app/interfaces/api/labelProject';
import { EnableTag, Project } from 'src/app/interfaces/api/project';
import { ModuleType } from 'src/app/interfaces/shared/module';


@Injectable({
  providedIn: 'root'
})

export class GalleryTool {
  // Services
  private readonly projectService: ProjectService = inject(ProjectService);
  private readonly attachmentService: AttachmentService = inject(AttachmentService);
  private readonly labelProjectService: LabelProjectService = inject(LabelProjectService);
  private readonly labelService: LabelService = inject(LabelService);

  // Variables
  displayNames = signal<Record<string, any>>({
    risk: {},
    crane: {},
    // zone: {},
    // action: {},
    // worker: {},
  });

  tagNames = signal<Record<ModuleType, string[]>>({
    llm: [],
    risk: [],
    crane: [],
    zone: [],
    action: [],
    worker: [],
  });

  currentTagProject: ModuleType = "risk";
  tags!: Record<ModuleType, any[]>;
  labelProject?: LabelProject;
  labels: Label[] = [];

  // Signals
  readonly currentProject: Signal<Project> = this.projectService.currentProjectSignal;

  constructor() {
    // Effect: When currentProject changes, fetch tags and label data
    effect(() => {
      const project = this.currentProject();

      untracked(() => {
        if (!this.tags)
          this.getTagNames().then((tags) => { });
        else
          this.parseTagNames();
      });
    });
  }

  getLabelData(module: string): Promise<{ labelProject?: LabelProject, labels: Label[] }> {
    return new Promise(resolve => {
      if (this.labelProject && this.labelProject.name === module)
        return resolve({ labelProject: this.labelProject, labels: this.labels });
      // getLabelProjects caches the first response and ignores the query on cache hits,
      // so always load the full list and pick the requested module from it.
      this.labelProjectService.getLabelProjects({}).pipe(first()).subscribe((res: { labelProjects: LabelProject[] }) => {
        let { labelProjects } = res;
        this.labelProject = labelProjects.find(lp => lp.name === module);
        if (!this.labelProject)
          return resolve({ labelProject: undefined, labels: [] });

        this.labelService.getLabelByLabelProject(this.labelProject.name).pipe(first()).subscribe(labels => {
          this.labels = labels || [];
          this.labels.sort((a, b) => a.name.localeCompare(b.name));
          resolve({ labelProject: this.labelProject, labels: this.labels });
        });
      });
    })
  }

  getTagNames(doFetch?: boolean): Promise<Record<ModuleType, any[]>> {
    return new Promise((resolve) => {
      if (this.tags && !doFetch)
        return resolve(this.tagNames());

      this.attachmentService.getTags().pipe(first()).subscribe((tags: Record<ModuleType, any[]>) => {
        this.tags = tags;
        this.parseTagNames();
        resolve(this.tagNames());
      });
    })
  }

  parseTags(project: ModuleType, res: any[] = []) {
    let availableTags = res;

    if (availableTags.length) {
      let enabledTags = this.currentProject().enableTags;

      let tagNames = availableTags.map(t => t.name || t.tagName).concat(this.currentProject()?.predictionTags).filter(tagName => {
        const enableTag = enabledTags?.find((enableTag: EnableTag) => enableTag.name.toLowerCase() === tagName.toLowerCase());
        return enableTag ? enableTag.enabled : true;
      });

      tagNames.sort((a, b) => a.localeCompare(b));

      return tagNames;
    }
    return availableTags;

    // this.predictions = [...this.getDisplayNames(project as ModuleType)];

  }

  async parseTagNames() {
    if (!this.tags) return;

    let tags = this.tags;
    let tagNames: Record<ModuleType, string[]> = {
      llm: [],
      risk: [],
      crane: [],
      zone: [],
      action: [],
      worker: []
    };

    tagNames["crane"] = this.parseTags("crane", tags["crane"]);
    tagNames["risk"] = this.parseTags("risk", tags["risk"]);

    ["worker", "zone", "action"].forEach(project => {
      if (!tags[project as ModuleType] || !Array.isArray(tags[project as ModuleType])) return;

      let names: string[] = tags[project as ModuleType].map(t => t.name || t.tagName);
      names.sort((a, b) => a.localeCompare(b));

      tagNames = { ...tagNames, [project]: names }
    });

    let enabledTags = this.currentProject().enableTags;

    if (tags["action"] && enabledTags && enabledTags.length !== 0) {
      let filtered = tagNames.action.filter(tagName => {
        const enableTag = enabledTags.find((enableTag: EnableTag) => enableTag.name === tagName);
        return enableTag ? enableTag.enabled : true;
      });

      tagNames = { ...tagNames, action: filtered }
    }

    this.tagNames.set(tagNames);
    this.setDisplayNames(tagNames);
  }

  // The API derives displayName from name when a label has none.
  private isOwnDisplayName(name?: string, value?: string): boolean {
    const trimmed = (value ?? '').trim();

    return !!trimmed && !!name && trimmed != name;
  }

  setDisplayNames(tagNames: Record<ModuleType, string[]>) {
    const displayNames: Record<string, any> = { risk: {}, crane: {} };

    let project = this.currentProject();
    Object.keys(tagNames).forEach(module => {
      if (["crane", "risk"].indexOf(module) == -1 || !displayNames[module]) return;

      const names = tagNames[module as ModuleType];

      (this.tags?.[module as ModuleType] ?? []).forEach((label: any) => {
        if (!this.isOwnDisplayName(label?.name, label?.displayName)) return;

        let tagName = names.find(tagName => tagName == label.name);

        if (tagName)
          displayNames[module][tagName] = label.displayName.trim();
      });

      project.enableTags?.filter((tag: EnableTag) => tag.enabled
        && module == tag.module
        && this.isOwnDisplayName(tag.name, tag.displayName)).forEach((tag: EnableTag) => {
        let tagName = names.find(tagName => tagName == tag.name);

        if (tagName)
          displayNames[module][tagName] = tag.displayName.trim();
      });
    })

    this.displayNames.set(displayNames);
  }

  getDisplayName(tagName: string, project: ModuleType = "risk"): any {
    if (!tagName) {
      return "";
    }

    project = project ?? this.currentTagProject;

    if (!["crane", "risk"].includes(project!) || !this.hasDisplayName(tagName))
      return tagName;

    const displayNames = this.displayNames()[project];

    if (displayNames && displayNames[tagName])
      return displayNames[tagName] || tagName;

    return this.displayNames()[this.isCraneTag(tagName) ? "crane" : "risk"][tagName] || tagName;
  }

  getDisplayNames(project: ModuleType): string[] {
    let tagNames = this.tagNames()[project];
    if (!tagNames) return [];

    if (["crane", "risk"].indexOf(project) == -1)
      return tagNames;

    const displayNames = this.displayNames()[project];
    return tagNames.map(tagName =>
      displayNames && displayNames[tagName]
        ? displayNames[tagName]
        : tagName
    );
  }

  getTagName(displayName: string, project?: ModuleType) {
    let projects = project ? [project] : ["crane", "risk"];

    for (let _project of projects) {
      let displayNames = this.displayNames()[_project]

      if (displayNames) {
        for (let [tagName, dName] of Object.entries(displayNames)) {
          if (dName === displayName) {
            return tagName;
          }
        }
      }

    }

    return displayName;
  }

  hasDisplayName(tagName: string): boolean {
    if (!this.displayNames) return false;

    return Object.values(this.displayNames()).some(displayNames => displayNames && displayNames[tagName]);
  }

  //check if a tag box is a type of given module
  isModuleTag(box: any, model: ModuleType) {
    return box.project ? box.project == model : this.tagNames()[model].includes(box.tagName || box.name);
  }

  isRiskTag(box: any) {
    return box.project ? box.project == 'risk' : this.tagNames()["risk"].includes(box.tagName || box.name);
  }

  isWorkerTag(box: any) {
    return box.project ? box.project == 'worker' : this.tagNames()["worker"].includes(box.tagName || box.name);
  }

  isCraneTag(box: any) {
    return box.project ? box.project == 'crane' : this.tagNames()["crane"].includes(box.tagName || box.name || box);
  }

}